import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useXmpEdit } from './hooks/useXmpEdit';

function DropZone({ files, onFilesSelect, disabled }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      f.name.toLowerCase().endsWith('.pdf')
    );
    if (dropped.length) onFilesSelect(dropped);
  }, [onFilesSelect, disabled]);

  const handleChange = useCallback((e) => {
    const selected = Array.from(e.target.files);
    if (selected.length) onFilesSelect(selected);
    e.target.value = '';
  }, [onFilesSelect]);

  return (
    <Paper
      sx={{
        p: 6,
        mb: 3,
        border: dragActive ? '3px dashed #1976d2' : '2px dashed #ccc',
        backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        transition: 'all 0.3s ease',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        textAlign: 'center',
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
      {files.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6">{files.length} PDF{files.length > 1 ? 's' : ''} selected</Typography>
          <Typography variant="body2" color="text.secondary">
            {files.map((f) => f.name).join(', ')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drop more files or click to replace
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? 'primary.main' : 'text.secondary' }} />
          <Typography variant="h5" color={dragActive ? 'primary.main' : 'text.primary'}>
            {dragActive ? 'Drop PDFs here' : 'Drag & Drop PDFs'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select — multiple files supported
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function ResultRow({ row }) {
  const [open, setOpen] = useState(false);
  const changes = row.changes ?? [];
  const hasChanges = changes.length > 0;
  const totalValues = changes.reduce((sum, c) => sum + (c.values?.length ?? 0), 0);

  return (
    <>
      <TableRow>
        <TableCell>{row.filename}</TableCell>
        <TableCell>
          {row.status === 'success' ? (
            <Chip icon={<CheckCircleIcon />} label="Success" color="success" size="small" />
          ) : (
            <Chip icon={<ErrorIcon />} label={row.message || 'Error'} color="error" size="small" />
          )}
        </TableCell>
        <TableCell>
          {hasChanges ? (
            <Chip
              label={`${totalValues} change${totalValues !== 1 ? 's' : ''}`}
              size="small"
              onClick={() => setOpen((o) => !o)}
              onDelete={() => setOpen((o) => !o)}
              deleteIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              sx={{ cursor: 'pointer' }}
            />
          ) : '—'}
        </TableCell>
        <TableCell>
          {row.s3Url ? (
            <Button
              variant="outlined"
              size="small"
              href={row.s3Url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </Button>
          ) : '—'}
        </TableCell>
      </TableRow>
      {hasChanges && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ py: 1.5, px: 2 }}>
                {changes.map((change) => (
                  <Typography key={change.field} variant="body2" sx={{ mb: 0.5 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>{change.field}: </Box>
                    {(change.values ?? []).join(', ')}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ResultsTable({ results }) {
  return (
    <TableContainer component={Paper} sx={{ mt: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Filename</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Changes</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((row) => (
            <ResultRow key={row.filename} row={row} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PdfXmpEditor() {
  const [files, setFiles] = useState([]);
  const { editFiles, results, loading, error } = useXmpEdit();

  const handleSubmit = () => editFiles(files);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        XMP Ingestion
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload one or more PDFs to inject copyright holder metadata into their XMP stream.
      </Typography>

      <DropZone files={files} onFilesSelect={setFiles} disabled={loading} />

      <Button
        variant="contained"
        size="large"
        disabled={files.length === 0 || loading}
        onClick={handleSubmit}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
      >
        {loading ? 'Processing…' : `Edit PDF${files.length !== 1 ? 's' : ''}`}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {results.length > 0 && <ResultsTable results={results} />}
    </Box>
  );
}

export default PdfXmpEditor;
